import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Users } from 'lucide-react';
import { itineraryCount } from '../utils/itinerary';
import { bfPageView } from '../utils/bfPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Badge is DISPLAYED only on core marketplace surfaces
// Badge hidden on /experience/ detail pages — it overlapped the Book Now button
const SHOW_ON = [/^\/marketplace/, /^\/search/];

// Presence pings + BF pixel PageViews cover the wider funnel (landing, trips,
// cart, profiles) so those viewers still COUNT — they just don't see the badge
const PING_ON = [
  /^\/$/, /^\/marketplace/, /^\/search/, /^\/experience\//, /^\/my-trips/,
  /^\/cart\//, /^\/host\//, /^\/find-booking/, /^\/support/, /^\/booking-confirmed/,
];

const deviceId = () => {
  try {
    let id = localStorage.getItem('bf_presence_id');
    if (!id) {
      id = 'pv_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('bf_presence_id', id);
    }
    return id;
  } catch {
    return 'pv_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
};

// Module-level guard: exactly ONE PageView per route, immune to StrictMode
// double-effects and component remounts.
let lastPvPath = null;

// 12438 → "12.4k", 980 → "980"
const compactNumber = (n) =>
  n >= 1000000 ? `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`
  : n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`
  : `${n}`;

export const LiveViewersBadge = () => {
  const location = useLocation();
  const [live, setLive] = useState(0);
  const [totalVisitors, setTotalVisitors] = useState(0);
  const [hasCart, setHasCart] = useState(false);
  const pinging = PING_ON.some((r) => r.test(location.pathname));
  const showing = SHOW_ON.some((r) => r.test(location.pathname));

  // BookingFlows pixel PageView on every marketplace/landing route change
  useEffect(() => {
    if (pinging && lastPvPath !== location.pathname) {
      lastPvPath = location.pathname;
      bfPageView();
    }
  }, [location.pathname, pinging]);

  // Presence heartbeat — one row per device, 180s liveness window (slow decay)
  useEffect(() => {
    if (!pinging) return;
    let stopped = false;
    const ping = async () => {
      if (stopped || document.visibilityState === 'hidden') return;
      try {
        const r = await fetch(`${API_URL}/api/v2/public/presence/ping`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: deviceId(), surface: 'marketplace' }),
        });
        if (r.ok) {
          const d = await r.json();
          if (typeof d.live === 'number') setLive(d.live);
          if (typeof d.totalVisitors === 'number') setTotalVisitors(d.totalVisitors);
        }
      } catch { /* offline — retry next tick */ }
    };
    ping();
    const t = setInterval(() => { if (!document.hidden) ping(); }, 20000);
    const onVis = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { stopped = true; clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [pinging]);

  // Stack above the floating cart button when it's present
  useEffect(() => {
    if (!showing) return;
    const check = () => { try { setHasCart(itineraryCount() > 0); } catch { setHasCart(false); } };
    check();
    const t = setInterval(check, 3000);
    return () => clearInterval(t);
  }, [showing]);

  if (!showing || live < 1) return null;

  return (
    <div
      className={`fixed right-6 z-[65] transition-all duration-300 ${hasCart ? 'bottom-[5.5rem]' : 'bottom-6'}`}
      data-testid="live-viewers-badge"
    >
      <div className="flex items-center gap-2 pl-3 pr-3.5 py-2 rounded-full bg-gray-900/90 backdrop-blur text-white shadow-soft">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
        </span>
        <Users className="w-3.5 h-3.5 text-gray-300" />
        <span className="text-xs font-semibold tracking-wide">LIVE</span>
        <span className="text-xs font-bold text-green-300" data-testid="live-viewers-count">{live}</span>
        {totalVisitors >= 1 && (
          <>
            <span className="text-gray-500 text-xs">·</span>
            <span className="text-xs text-gray-300" data-testid="total-visitors-count">
              {compactNumber(totalVisitors)} visitors
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default LiveViewersBadge;
